import type { ReactNode } from 'react';
import './vitrine.css';
import { C, POLICE_TITRE } from '../theme';
import { Micro, Coche, Horloge, Cloche, Panier, Enveloppe, CalendrierCoche } from '../icones';
import { EnTete, Lien, Pied } from './composants';
import { CHEMIN_CONFIDENTIALITE } from '../routeur';

/**
 * Page « Comment ça marche » : le parcours complet, illustre.
 *
 * Les ecrans ci-dessous sont des ILLUSTRATIONS, redessinees avec la palette et les
 * composants de l'application. Ce ne sont pas des captures : quand l'application evolue,
 * elles doivent etre reprises a la main, sinon la vitrine promet autre chose que ce qui
 * est livre.
 */

const PHRASE = "Jeudi je vais à Lyon pour une réunion à 10h. Il faut le dossier et le chargeur.";

export function Demo() {
  return (
    <div className="v-page">
      <EnTete
        actionPrincipale={
          <Lien vers="/app" className="v-bouton v-bouton--secondaire v-bouton--compact">
            Ouvrir Colette
          </Lien>
        }
      />

      <main>
        <section className="v-section v-section--serree">
          {/* Meme contenance que les etapes plus bas : le bord gauche du texte doit
              tomber exactement au meme endroit sur toute la page. La largeur de lecture
              est bornee a l'interieur, pas par un conteneur centre. */}
          <div className="v-contenu">
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--e4)',
                maxWidth: '52ch',
              }}
            >
              <p className="v-surtitre">Comment ça marche</p>
              <h1 className="v-h1" style={{ fontSize: 'clamp(1.875rem, 4.8vw, 3rem)' }}>
                Une phrase, et ta semaine se range
              </h1>
              <p className="v-lead">
                Voici exactement ce qui se passe entre le moment où tu parles à Colette et celui
                où ton planning est à jour. Trois écrans, pas un de plus.
              </p>
            </div>
          </div>
        </section>

        <Etape
          numero={1}
          titre="Tu lui dis ce que tu as en tête"
          texte="Au moment où tu y penses, en marchant, en cuisinant. Tu dictes ou tu écris — Colette accepte les phrases telles qu'elles viennent, sans mot-clé ni formulaire."
          points={[
            'Pas de champ « date », pas de menu déroulant.',
            'Tu peux tout mettre dans la même phrase.',
            'Le message est gardé tel que tu l’as dit.',
          ]}
          ecran={<EcranDictee />}
        />

        <Etape
          numero={2}
          titre="Elle te montre ce qu’elle a compris"
          texte="Colette découpe ta phrase en éléments concrets et te les présente un par un. Rien n’est encore enregistré : c’est une proposition, pas une action."
          points={[
            'Un rendez-vous, une tâche, un rappel, une liste — chacun séparé.',
            'Tu vois ta phrase d’origine juste au-dessus.',
            'Tu valides ou tu refuses, proposition par proposition.',
          ]}
          ecran={<EcranCompris />}
          inverse
        />

        <Etape
          numero={3}
          titre="Ce que tu valides atterrit dans ton planning"
          texte="Et seulement ce que tu as validé. Colette te préviendra au bon moment — la veille pour préparer le sac, le matin pour partir à l’heure."
          points={[
            'Ton planning reste le tien : rien n’y entre sans ton accord.',
            'Les listes sont rattachées à l’événement qui les concerne.',
            'Tu peux corriger à tout moment.',
          ]}
          ecran={<EcranPlanning />}
        />

        {/* --- Autres exemples --- */}
        <section className="v-section v-section--carte">
          <div className="v-contenu">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--e3)', maxWidth: '46ch' }}>
              <p className="v-surtitre">Le même principe, partout</p>
              <h2 className="v-h2">D’autres phrases du quotidien</h2>
            </div>

            <div className="v-grille v-grille--2" style={{ marginTop: 'var(--e6)' }}>
              <Exemple
                icone={<Enveloppe taille={20} couleur={C.violetTexte} epaisseur={1.9} />}
                fond={C.violetFond}
                phrase="Sortie scolaire jeudi. Prévoir casquette, gourde, pique-nique et autorisation signée. RDV 8h15."
                resultats={[
                  'Événement — jeudi 8h15, sortie scolaire',
                  'Tâche — signer l’autorisation avant mercredi soir',
                  'Rappel — mercredi 19h, préparer le sac',
                  'Liste — casquette, gourde, pique-nique',
                ]}
              />
              <Exemple
                icone={<Panier taille={20} couleur={C.mentheTexte} epaisseur={1.9} />}
                fond={C.mentheFond}
                phrase="Vendredi soir on fait un poulet yassa."
                resultats={[
                  'Événement — vendredi 19h30, dîner',
                  'Liste de courses — oignons, citrons, moutarde',
                ]}
              />
              <Exemple
                icone={<CalendrierCoche taille={20} couleur={C.corailTexte} epaisseur={1.9} />}
                fond={C.corailFond}
                phrase="La carte d’identité de Lila expire en mars prochain."
                resultats={[
                  'Échéance — mars, expiration de la carte d’identité',
                  'Rappel — deux mois avant, prendre rendez-vous en mairie',
                ]}
              />
              <Exemple
                icone={<Horloge taille={20} couleur={C.violetTexte} epaisseur={1.9} />}
                fond={C.violetFond}
                phrase="Dentiste mardi prochain à 15h pour les enfants."
                resultats={[
                  'Événement — mardi 15h, dentiste',
                  'Rappel — la veille au soir',
                ]}
              />
            </div>
          </div>
        </section>

        {/* --- Garantie --- */}
        <section className="v-section v-section--serree">
          <div
            className="v-contenu v-contenu--etroit"
            style={{ display: 'flex', flexDirection: 'column', gap: 'var(--e4)', textAlign: 'center', alignItems: 'center' }}
          >
            <span
              style={{
                width: 52,
                height: 52,
                borderRadius: 16,
                background: C.mentheFond,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Coche taille={26} couleur={C.mentheTexte} epaisseur={2.4} />
            </span>
            <h2 className="v-h2">Colette ne fait jamais rien toute seule</h2>
            <p className="v-lead" style={{ textAlign: 'center' }}>
              Chaque proposition attend ton accord. C’est plus lent qu’un assistant qui décide à ta
              place — et c’est exactement le but.{' '}
              <Lien vers={CHEMIN_CONFIDENTIALITE}>Ce qu’elle fait de tes données</Lien>.
            </p>
            <div className="v-boutons" style={{ justifyContent: 'center', marginTop: 'var(--e2)' }}>
              <Lien vers="/" className="v-bouton v-bouton--principal">
                Installer Colette
              </Lien>
            </div>
          </div>
        </section>
      </main>

      <Pied />
    </div>
  );
}

// --- Mise en page d'une etape ------------------------------------------------

function Etape({
  numero,
  titre,
  texte,
  points,
  ecran,
  inverse = false,
}: {
  numero: number;
  titre: string;
  texte: string;
  points: string[];
  ecran: ReactNode;
  inverse?: boolean;
}) {
  return (
    <section className="v-section v-section--serree">
      <div className="v-contenu v-hero">
        <div className="v-hero__texte" style={{ order: inverse ? 2 : 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--e3)' }}>
            <span className="v-etape__numero">{numero}</span>
            <p className="v-surtitre" style={{ margin: 0 }}>
              Étape {numero}
            </p>
          </div>
          <h2 className="v-h2">{titre}</h2>
          <p className="v-lead">{texte}</p>
          <ul className="v-liste" style={{ marginTop: 'var(--e2)' }}>
            {points.map((point) => (
              <li key={point}>
                <span style={{ flex: 'none', marginTop: 2 }}>
                  <Coche taille={17} couleur={C.menthe} epaisseur={2.6} />
                </span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
        <div style={{ order: inverse ? 1 : 2 }}>{ecran}</div>
      </div>
    </section>
  );
}

function Exemple({
  icone,
  fond,
  phrase,
  resultats,
}: {
  icone: ReactNode;
  fond: string;
  phrase: string;
  resultats: string[];
}) {
  return (
    <div className="v-carte">
      <span className="v-carte__icone" style={{ background: fond }}>
        {icone}
      </span>
      <p
        style={{
          margin: 0,
          fontSize: '0.9375rem',
          fontStyle: 'italic',
          lineHeight: 1.55,
          color: C.encre,
        }}
      >
        « {phrase} »
      </p>
      <div style={{ height: 1, background: C.bordure }} />
      <ul className="v-liste">
        {resultats.map((resultat) => (
          <li key={resultat} style={{ fontSize: '0.875rem', color: C.secondaire }}>
            <span style={{ flex: 'none', marginTop: 3 }}>
              <Coche taille={15} couleur={C.menthe} epaisseur={2.6} />
            </span>
            <span>{resultat}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// --- Illustrations d'ecrans --------------------------------------------------

function Chassis({ children, etiquette }: { children: ReactNode; etiquette: string }) {
  return (
    <div className="v-tel" role="img" aria-label={etiquette}>
      <div className="v-tel__ecran">{children}</div>
    </div>
  );
}

function TitreEcran({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontFamily: POLICE_TITRE,
        fontWeight: 600,
        fontSize: 17,
        letterSpacing: '-0.01em',
        color: C.encre,
        marginBottom: 2,
      }}
    >
      {children}
    </div>
  );
}

function EcranDictee() {
  return (
    <Chassis etiquette="Écran de dictée : le micro écoute et la phrase s’écrit">
      <TitreEcran>Organise-moi ça</TitreEcran>
      <div style={{ display: 'flex', gap: 6 }}>
        <span
          style={{
            flex: 1,
            height: 28,
            borderRadius: 8,
            background: C.violetFond,
            color: C.violetTexte,
            fontSize: 11,
            fontWeight: 800,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          Dicter
        </span>
        <span
          style={{
            flex: 1,
            height: 28,
            borderRadius: 8,
            background: C.carte,
            color: C.tertiaire,
            fontSize: 11,
            fontWeight: 800,
            display: 'grid',
            placeItems: 'center',
          }}
        >
          Écrire
        </span>
      </div>

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
          padding: '14px 0 6px',
        }}
      >
        <span
          style={{
            width: 58,
            height: 58,
            borderRadius: '50%',
            background: C.violet,
            display: 'grid',
            placeItems: 'center',
            boxShadow: `0 0 0 10px ${C.violetFond}`,
          }}
        >
          <Micro taille={26} couleur="#FFFFFF" />
        </span>
        <div style={{ fontSize: 10.5, color: C.secondaire }}>Je t’écoute…</div>
      </div>

      <div
        style={{
          background: C.page,
          borderRadius: 10,
          padding: '10px 11px',
          fontSize: 11.5,
          lineHeight: 1.5,
          color: C.encre,
        }}
      >
        {PHRASE}
        <span style={{ color: C.violet, fontWeight: 800 }}>▍</span>
      </div>

      <div
        style={{
          marginTop: 'auto',
          height: 38,
          borderRadius: 11,
          background: C.violet,
          color: '#FFFFFF',
          fontSize: 12.5,
          fontWeight: 800,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        Organiser
      </div>
    </Chassis>
  );
}

function MiniProposition({
  tag,
  titre,
  detail,
  puces,
}: {
  tag: string;
  titre: string;
  detail?: string;
  puces?: string[];
}) {
  return (
    <div
      style={{
        background: C.carte,
        border: `1px solid ${C.bordure}`,
        borderRadius: 10,
        padding: '9px 10px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span
          style={{
            alignSelf: 'flex-start',
            background: C.violetFond,
            color: C.violetTexte,
            fontSize: 8,
            fontWeight: 800,
            letterSpacing: 0.5,
            padding: '2px 7px',
            borderRadius: 999,
          }}
        >
          {tag}
        </span>
        <div style={{ fontSize: 11.5, fontWeight: 700, color: C.encre, lineHeight: 1.25 }}>
          {titre}
        </div>
        {detail !== undefined ? (
          <div style={{ fontSize: 9.5, color: C.secondaire, lineHeight: 1.3 }}>{detail}</div>
        ) : null}
        {puces !== undefined ? (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
            {puces.map((puce) => (
              <span
                key={puce}
                style={{
                  border: `1px solid ${C.bordure}`,
                  borderRadius: 999,
                  padding: '2px 7px',
                  fontSize: 9,
                  color: C.encre,
                }}
              >
                {puce}
              </span>
            ))}
          </div>
        ) : null}
      </div>
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          border: `1px solid ${C.bordure}`,
          display: 'grid',
          placeItems: 'center',
          flex: 'none',
        }}
      >
        <Coche taille={14} couleur={C.menthe} epaisseur={2.6} />
      </span>
    </div>
  );
}

function EcranCompris() {
  return (
    <Chassis etiquette="Écran des propositions à valider">
      <TitreEcran>Voici ce que j’ai compris</TitreEcran>
      <div
        style={{
          background: C.page,
          borderRadius: 9,
          padding: '8px 10px',
          fontSize: 10,
          fontStyle: 'italic',
          lineHeight: 1.4,
          color: C.secondaire,
        }}
      >
        « {PHRASE} »
      </div>

      <MiniProposition tag="ÉVÉNEMENT" titre="Réunion à Lyon" detail="Jeudi 27 août · 10:00 · Lyon" />
      <MiniProposition tag="RAPPEL" titre="Partir à 07:05" detail="Mercredi · 07:05" />
      <MiniProposition tag="TÂCHE" titre="Préparer le dossier" detail="Mercredi soir" />
      <MiniProposition tag="CHECKLIST" titre="Sac pour Lyon" puces={['dossier', 'chargeur']} />

      <div
        style={{
          marginTop: 'auto',
          height: 36,
          borderRadius: 11,
          background: C.violet,
          color: '#FFFFFF',
          fontSize: 12,
          fontWeight: 800,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        Tout valider
      </div>
    </Chassis>
  );
}

function EcranPlanning() {
  return (
    <Chassis etiquette="Écran du planning, une fois les propositions validées">
      <TitreEcran>Planning</TitreEcran>

      <div
        style={{
          fontSize: 8.5,
          fontWeight: 800,
          letterSpacing: 0.7,
          textTransform: 'uppercase',
          color: C.secondaire,
          marginTop: 2,
        }}
      >
        Mercredi 26 août
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 2px' }}>
        <Cloche taille={14} couleur={C.violet} epaisseur={1.9} />
        <span style={{ fontSize: 11, color: C.encre }}>
          <strong>19:00</strong> — Préparer le dossier
        </span>
      </div>

      <div
        style={{
          fontSize: 8.5,
          fontWeight: 800,
          letterSpacing: 0.7,
          textTransform: 'uppercase',
          color: C.secondaire,
          marginTop: 4,
        }}
      >
        Jeudi 27 août
      </div>
      <div
        style={{
          background: C.violetFond,
          borderRadius: 11,
          padding: '10px 11px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: C.violetTexte }}>07:05</span>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: C.encre }}>Départ pour Lyon</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: C.violetTexte }}>10:00</span>
          <span style={{ fontSize: 11.5, fontWeight: 700, color: C.encre }}>Réunion à Lyon</span>
        </div>
        <div
          style={{
            background: C.carte,
            borderRadius: 8,
            padding: '8px 9px',
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
          }}
        >
          <div style={{ fontSize: 9, fontWeight: 800, color: C.violetTexte }}>Sac pour Lyon</div>
          {['dossier', 'ordinateur', 'chargeur'].map((element) => (
            <div key={element} style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <span
                style={{
                  width: 13,
                  height: 13,
                  borderRadius: 4,
                  border: `2px solid ${C.violetBordure}`,
                  flex: 'none',
                }}
              />
              <span style={{ fontSize: 10.5, color: C.encre }}>{element}</span>
            </div>
          ))}
        </div>
      </div>
    </Chassis>
  );
}
